import type { XLMCellAddress, XLMModel, XLMNode, XLMValue } from "../api/e2e";
import { displayXLMUnit, formatXLMNumber } from "./xlmDisplay";

export const MODEL_REFERENCE_PREFIX = "fdr-model://";
export const MODEL_REFERENCE_PARAM = "model-ref";
export const MODEL_REFERENCE_INSPECT_PARAM = "model-inspect";

export type ModelReferenceCadence = "annual" | "quarterly" | "half-year";

export interface ModelReferencePeriod {
  cadence: ModelReferenceCadence;
  year: number;
  part?: number;
}

export interface ModelReferenceCell {
  identifier: string;
  period: ModelReferencePeriod;
}

export interface ModelReference {
  kind: "cell" | "range";
  start: ModelReferenceCell;
  end: ModelReferenceCell;
}

export interface ModelDocumentCellMention {
  documentId: string;
  lineItemLabel: string;
  periodLabel: string;
  valueLabel: string;
  comment: string | null;
  evidence: string | null;
  excerpts: string[];
  reference: ModelReference;
}

function parseIdentifier(value: string): string | null {
  try {
    const identifier = decodeURIComponent(value);
    if (
      identifier.length === 0 ||
      identifier.length > 512 ||
      identifier.includes("/") ||
      /[\u0000-\u001f\u007f]/u.test(identifier)
    ) {
      return null;
    }
    return identifier;
  } catch {
    return null;
  }
}

function parsePeriod(value: string): ModelReferencePeriod | null {
  const match = /^(?<year>[0-9]{4})(?:(?<cadence>[QH])(?<part>[1-4]))?$/.exec(
    value,
  );
  if (!match?.groups) return null;
  const year = Number(match.groups.year);
  const cadenceToken = match.groups.cadence;
  const part = match.groups.part ? Number(match.groups.part) : undefined;
  if (!cadenceToken) return { cadence: "annual", year };
  if (cadenceToken === "Q" && part !== undefined) {
    return { cadence: "quarterly", year, part };
  }
  if (cadenceToken === "H" && (part === 1 || part === 2)) {
    return { cadence: "half-year", year, part };
  }
  return null;
}

function parseCell(
  identifierValue: string,
  periodValue: string,
): ModelReferenceCell | null {
  const identifier = parseIdentifier(identifierValue);
  const period = parsePeriod(periodValue);
  if (!identifier || !period) return null;
  return { identifier, period };
}

export function parseModelReference(
  value: string | null | undefined,
): ModelReference | null {
  if (!value) return null;
  const target = value.startsWith(MODEL_REFERENCE_PREFIX)
    ? value.slice(MODEL_REFERENCE_PREFIX.length)
    : value;
  if (target.includes("?") || target.includes("#") || target.includes("\\")) {
    return null;
  }
  const parts = target.split("/");
  if (parts[0] === "cell" && parts.length === 3) {
    const cell = parseCell(parts[1] ?? "", parts[2] ?? "");
    return cell ? { kind: "cell", start: cell, end: cell } : null;
  }
  if (parts[0] !== "range" || parts.length !== 5) return null;
  const start = parseCell(parts[1] ?? "", parts[2] ?? "");
  const end = parseCell(parts[3] ?? "", parts[4] ?? "");
  if (!start || !end || start.period.cadence !== end.period.cadence) {
    return null;
  }
  return { kind: "range", start, end };
}

function periodToken(period: ModelReferencePeriod): string {
  if (period.cadence === "quarterly") {
    return `${period.year}Q${period.part}`;
  }
  if (period.cadence === "half-year") {
    return `${period.year}H${period.part}`;
  }
  return String(period.year);
}

export function modelReferencePeriodLabel(
  period: ModelReferencePeriod,
): string {
  if (period.cadence === "quarterly") {
    return `Q${period.part} FY${period.year}`;
  }
  if (period.cadence === "half-year") {
    return `H${period.part} FY${period.year}`;
  }
  return `FY${period.year}`;
}

export function modelReferenceTarget(reference: ModelReference): string {
  const start = `${encodeURIComponent(reference.start.identifier)}/${periodToken(reference.start.period)}`;
  if (reference.kind === "cell") return `cell/${start}`;
  const end = `${encodeURIComponent(reference.end.identifier)}/${periodToken(reference.end.period)}`;
  return `range/${start}/${end}`;
}

export function modelReferenceUri(reference: ModelReference): string {
  return `${MODEL_REFERENCE_PREFIX}${modelReferenceTarget(reference)}`;
}

export function modelReferenceFromCellAddress(
  address: XLMCellAddress,
): ModelReference | null {
  let period: ModelReferencePeriod;
  if (address.collection.startsWith("quarterly")) {
    if (address.index === null || address.index < 1 || address.index > 4) {
      return null;
    }
    period = {
      cadence: "quarterly",
      year: address.year,
      part: address.index,
    };
  } else if (address.collection.startsWith("half_year")) {
    if (address.index !== 1 && address.index !== 2) return null;
    period = {
      cadence: "half-year",
      year: address.year,
      part: address.index,
    };
  } else {
    period = { cadence: "annual", year: address.year };
  }
  const cell = { identifier: address.identifier, period };
  return { kind: "cell", start: cell, end: cell };
}

export function openModelReferenceParams(
  current: URLSearchParams,
  reference: ModelReference,
  options: { inspect?: boolean } = {},
): URLSearchParams {
  const next = new URLSearchParams(current);
  next.set("tab", "model");
  next.set(MODEL_REFERENCE_PARAM, modelReferenceTarget(reference));
  if (options.inspect) next.set(MODEL_REFERENCE_INSPECT_PARAM, "true");
  else next.delete(MODEL_REFERENCE_INSPECT_PARAM);
  return next;
}

export function clearModelReferenceParams(
  current: URLSearchParams,
): URLSearchParams {
  const next = new URLSearchParams(current);
  next.delete(MODEL_REFERENCE_PARAM);
  next.delete(MODEL_REFERENCE_INSPECT_PARAM);
  return next;
}

export function sameModelReferencePeriod(
  left: ModelReferencePeriod,
  right: ModelReferencePeriod,
): boolean {
  return (
    left.cadence === right.cadence &&
    left.year === right.year &&
    left.part === right.part
  );
}

function nodeValues(node: XLMNode, cadence: ModelReferenceCadence): XLMValue[] {
  if (cadence === "annual") return node.yearly_values ?? [];
  if (cadence === "quarterly") return node.quarterly_values ?? [];
  return node.half_year_values ?? [];
}

function reportedNodeValues(
  node: XLMNode,
  cadence: ModelReferenceCadence,
): XLMValue[] {
  if (cadence === "annual") {
    return node.breakdown?.yearly_reported_totals ?? [];
  }
  if (cadence === "quarterly") {
    return node.breakdown?.quarterly_reported_totals ?? [];
  }
  return node.breakdown?.half_year_reported_totals ?? [];
}

function valuePeriodKey(
  value: XLMValue,
  cadence: ModelReferenceCadence,
): string {
  if (cadence === "quarterly") return `${value.year}Q${value.quarter ?? 0}`;
  if (cadence === "half-year") return `${value.year}H${value.half ?? 0}`;
  return String(value.year);
}

/** Match the model grid: a reported breakdown total occupies the parent
 * line-item cell for the same period and takes precedence over a direct value. */
function displayedNodeValues(
  node: XLMNode,
  cadence: ModelReferenceCadence,
): XLMValue[] {
  const values = new Map(
    nodeValues(node, cadence).map((value) => [
      valuePeriodKey(value, cadence),
      value,
    ]),
  );
  for (const value of reportedNodeValues(node, cadence)) {
    values.set(valuePeriodKey(value, cadence), value);
  }
  return [...values.values()];
}

function populated(value: XLMValue): boolean {
  const resolved = value.computed_value ?? value.value;
  return typeof resolved === "number" || typeof resolved === "boolean";
}

function nodeDisplayUnit(
  model: XLMModel,
  node: XLMNode,
): { unit: string | null; unitLabel: string | null } {
  for (const parent of model.nodes) {
    const parentUnit = parent.breakdown?.unit ?? parent.unit ?? null;
    if (parent === node) {
      return { unit: parentUnit, unitLabel: null };
    }
    if ((parent.breakdown?.items ?? []).includes(node)) {
      return {
        unit: node.unit ?? parentUnit,
        unitLabel: null,
      };
    }
  }
  return { unit: node.unit ?? null, unitLabel: null };
}

function modelCellValueLabel(
  model: XLMModel,
  node: XLMNode,
  value: XLMValue,
): string {
  const resolved = value.computed_value ?? value.value;
  if (typeof resolved === "boolean") return resolved ? "TRUE" : "FALSE";
  if (typeof resolved !== "number") return "—";
  const { unit, unitLabel } = nodeDisplayUnit(model, node);
  const valueLabel = formatXLMNumber(resolved, unit ?? unitLabel);
  const normalizedUnit = (unit ?? unitLabel)?.trim().toLocaleLowerCase() ?? "";
  if (["percent", "%", "multiple", "x", "boolean"].includes(normalizedUnit)) {
    return valueLabel;
  }
  const renderedUnit = displayXLMUnit(
    unit,
    unitLabel,
    model.reporting_currency,
  );
  return renderedUnit === "—" ? valueLabel : `${valueLabel} ${renderedUnit}`;
}

function referenceableNodes(
  model: XLMModel,
  cadence: ModelReferenceCadence,
): XLMNode[] {
  const result: XLMNode[] = [];
  for (const node of model.nodes) {
    const structural = ["section_title", "separator"].includes(node.type);
    const emptyAuthoredRow =
      !node.breakdown && ["truth", "computed", "total"].includes(node.type);
    if (
      !structural &&
      (displayedNodeValues(node, cadence).some(populated) || emptyAuthoredRow)
    ) {
      result.push(node);
    }
    for (const item of node.breakdown?.items ?? []) {
      if (
        nodeValues(item, cadence).some(populated) ||
        ["truth", "computed", "total"].includes(item.type)
      ) {
        result.push(item);
      }
    }
  }
  return result;
}

function referenceNode(
  model: XLMModel,
  cell: ModelReferenceCell,
): XLMNode | undefined {
  if (
    !modelPeriods(model, cell.period.cadence).some((period) =>
      sameModelReferencePeriod(period, cell.period),
    )
  ) {
    return undefined;
  }
  return referenceableNodes(model, cell.period.cadence).find(
    (node) => node.identifier === cell.identifier,
  );
}

export function modelReferenceDescription(
  model: XLMModel,
  reference: ModelReference,
): string | null {
  const start = referenceNode(model, reference.start);
  const end = referenceNode(model, reference.end);
  if (!start || !end) return null;
  const startLabel = `${start.name} · ${modelReferencePeriodLabel(reference.start.period)}`;
  if (reference.kind === "cell") return startLabel;
  const endLabel = `${end.name} · ${modelReferencePeriodLabel(reference.end.period)}`;
  return `${startLabel} → ${endLabel}`;
}

function modelPeriods(
  model: XLMModel,
  cadence: ModelReferenceCadence,
): ModelReferencePeriod[] {
  if (cadence === "annual") {
    return [...model.fiscal_years]
      .sort((left, right) => left.year - right.year)
      .map(({ year }) => ({ cadence, year }));
  }
  if (cadence === "quarterly") {
    return [...model.quarters]
      .sort(
        (left, right) => left.year - right.year || left.quarter - right.quarter,
      )
      .map(({ year, quarter }) => ({ cadence, year, part: quarter }));
  }
  return [...model.half_years]
    .sort((left, right) => left.year - right.year || left.half - right.half)
    .map(({ year, half }) => ({ cadence, year, part: half }));
}

export function modelReferenceAvailable(
  model: XLMModel,
  reference: ModelReference,
): boolean {
  const cadence = reference.start.period.cadence;
  if (cadence !== reference.end.period.cadence) return false;
  const rows = referenceableNodes(model, cadence);
  const startRow = rows.findIndex(
    (node) => node.identifier === reference.start.identifier,
  );
  const endRow = rows.findIndex(
    (node) => node.identifier === reference.end.identifier,
  );
  if (startRow < 0 || endRow < startRow) return false;
  const periods = modelPeriods(model, cadence);
  const startPeriod = periods.findIndex((period) =>
    sameModelReferencePeriod(period, reference.start.period),
  );
  const endPeriod = periods.findIndex((period) =>
    sameModelReferencePeriod(period, reference.end.period),
  );
  return startPeriod >= 0 && endPeriod >= startPeriod;
}

/** Build the reverse source-evidence index for the exact pinned model.
 * Mentions are unique per document/cell and follow the grid's cadence, row,
 * and period order so the UI never guesses from catalog or run metadata. */
export function modelDocumentCellMentions(
  model: XLMModel,
): ModelDocumentCellMention[] {
  const mentions: ModelDocumentCellMention[] = [];
  const seen = new Set<string>();
  const cadences: ModelReferenceCadence[] = [
    "annual",
    "quarterly",
    "half-year",
  ];

  for (const cadence of cadences) {
    const periods = modelPeriods(model, cadence);
    for (const node of referenceableNodes(model, cadence)) {
      const values = displayedNodeValues(node, cadence);
      for (const period of periods) {
        const value = values.find((candidate) => {
          if (candidate.year !== period.year) return false;
          if (cadence === "quarterly") {
            return candidate.quarter === period.part;
          }
          if (cadence === "half-year") {
            return candidate.half === period.part;
          }
          return true;
        });
        if (!value || !populated(value)) continue;
        const cell = { identifier: node.identifier, period };
        const reference: ModelReference = {
          kind: "cell",
          start: cell,
          end: cell,
        };
        const target = modelReferenceTarget(reference);
        for (const documentReference of value.document_references ?? []) {
          const documentId = documentReference.document_id;
          const key = `${documentId}\u0000${target}`;
          if (seen.has(key)) continue;
          seen.add(key);
          mentions.push({
            documentId,
            lineItemLabel: node.name,
            periodLabel: modelReferencePeriodLabel(period),
            valueLabel: modelCellValueLabel(model, node, value),
            comment: value.comment?.trim() || null,
            evidence: documentReference.evidence?.trim() || null,
            excerpts: [
              ...new Set(
                documentReference.locations.flatMap((location) => {
                  const excerpt = location.excerpt?.trim();
                  return excerpt ? [excerpt] : [];
                }),
              ),
            ],
            reference,
          });
        }
      }
    }
  }

  return mentions;
}

const MODEL_FORMULA_REFERENCE_PATTERN =
  /(?<identifier>[A-Za-z_][A-Za-z0-9_]*)(?:\[(?<period>[0-9]{4}(?:Q[1-4]|H[12])?)\])?/g;

interface ModelFormulaReferenceMatch {
  start: number;
  end: number;
  reference: ModelReference;
}

function modelFormulaReferenceMatches(
  model: XLMModel,
  formula: string,
  currentPeriod: ModelReferencePeriod,
): ModelFormulaReferenceMatch[] {
  const matches: ModelFormulaReferenceMatch[] = [];
  for (const match of formula.matchAll(MODEL_FORMULA_REFERENCE_PATTERN)) {
    const start = match.index;
    const raw = match[0];
    const identifier = match.groups?.identifier;
    if (start === undefined || !identifier || raw === "") continue;
    const targetPeriod = match.groups?.period ?? periodToken(currentPeriod);
    const reference = parseModelReference(
      `cell/${encodeURIComponent(identifier)}/${targetPeriod}`,
    );
    if (!reference || !modelReferenceAvailable(model, reference)) continue;
    matches.push({ start, end: start + raw.length, reference });
  }
  return matches;
}

/** Return every valid model reference used by a formula. Colon-delimited
 * endpoints are promoted to one rectangular range so grid consumers can
 * include every cell between them rather than highlighting only the edges. */
export function modelFormulaReferences(
  model: XLMModel,
  formula: string,
  currentPeriod: ModelReferencePeriod,
): ModelReference[] {
  const matches = modelFormulaReferenceMatches(model, formula, currentPeriod);
  const references: ModelReference[] = [];
  for (let index = 0; index < matches.length; index += 1) {
    const current = matches[index];
    const next = matches[index + 1];
    if (!current) continue;
    if (next && /^\s*:\s*$/.test(formula.slice(current.end, next.start))) {
      const range: ModelReference = {
        kind: "range",
        start: current.reference.start,
        end: next.reference.end,
      };
      if (modelReferenceAvailable(model, range)) {
        references.push(range);
        index += 1;
        continue;
      }
    }
    references.push(current.reference);
  }
  return references;
}

export type ModelFormulaPart =
  | { kind: "text"; text: string }
  | {
      kind: "reference";
      raw: string;
      label: string;
      reference: ModelReference;
    };

function appendFormulaText(parts: ModelFormulaPart[], text: string): void {
  if (text === "") return;
  const previous = parts.at(-1);
  if (previous?.kind === "text") {
    previous.text += text;
    return;
  }
  parts.push({ kind: "text", text });
}

export function modelFormulaParts(
  model: XLMModel,
  formula: string,
  currentPeriod: ModelReferencePeriod,
): ModelFormulaPart[] {
  const parts: ModelFormulaPart[] = [];
  let cursor = 0;
  for (const match of formula.matchAll(MODEL_FORMULA_REFERENCE_PATTERN)) {
    const index = match.index;
    const raw = match[0];
    const identifier = match.groups?.identifier;
    if (index === undefined || !identifier || raw === "") continue;
    const targetPeriod = match.groups?.period ?? periodToken(currentPeriod);
    const reference = parseModelReference(
      `cell/${encodeURIComponent(identifier)}/${targetPeriod}`,
    );
    if (!reference || !modelReferenceAvailable(model, reference)) continue;
    const label = modelReferenceDescription(model, reference);
    if (!label) continue;
    appendFormulaText(parts, formula.slice(cursor, index));
    parts.push({ kind: "reference", raw, label, reference });
    cursor = index + raw.length;
  }
  appendFormulaText(parts, formula.slice(cursor));
  return parts;
}
